<template>
  <section class="plan" :class="classes">
    <VText class="plan__title" variant="h2">
      <slot name="title">{{ title }}</slot>
    </VText>
    <div class="plan__price">
      <slot name="price">
        <span>{{ formattedPrice }}</span> {{ priceDescription }}
      </slot>
    </div>
    <div class="plan__actions">
      <slot name="actions" />
      <div class="plan__actions__text">
        <slot name="below-actions" />
      </div>
    </div>
    <div class="plan__description">
      <div
        class="plan__description__text"
        v-if="$slots.description || description"
      >
        <slot name="description">
          <VText>{{ description }}</VText>
        </slot>
      </div>
      <ul class="plan__features">
        <slot name="features">
          <li v-for="feature in features" :key="feature.title">
            <FancyIconCircle
              class="plan__feature-icon"
              :color="feature.checked === false ? 'error' : 'success'"
              size=".75rem"
            >
              <CheckIcon
                v-if="feature.checked === undefined || feature.checked === true"
                size="1.25rem"
              />
              <XIcon v-if="feature.checked === false" size="1rem" />
            </FancyIconCircle>
            <span>{{ feature.title }}</span>
          </li>
        </slot>
      </ul>
    </div>

    <div class="plan__blocks" v-if="blocks && blocks.length">
      <div class="plan__block" v-for="({ icon, title }, i) in blocks" :key="i">
        <component :is="icon" />
        <span>{{ title }}</span>
      </div>
    </div>

    <Ribbon v-if="isHighlighted" color="secondary">Popular</Ribbon>
  </section>
</template>

<script>
import {
  VText,
  Ribbon,
  FancyIconCircle,
  CheckIcon,
  XIcon,
} from '@campsite-bio/component-lib';
import { computed } from '@nuxtjs/composition-api';

export default {
  components: {
    VText,
    Ribbon,
    FancyIconCircle,
    CheckIcon,
    XIcon,
  },

  props: {
    disabled: Boolean,
    isHighlighted: Boolean,

    title: {
      type: String,
      default: null,
    },

    description: {
      type: String,
      default: null,
    },

    price: {
      type: [String, Number],
      default: null,
    },

    priceDescription: {
      type: String,
      default: null,
    },

    features: {
      type: Array,
      default: () => [],
    },

    blocks: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const formattedPrice = computed(() => {
      return Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      })
        .format(props.price)
        .replace('.00', '');
    });

    const classes = computed(() => {
      return {
        'plan--highlight': props.isHighlighted,
        'plan--disabled': props.disabled,
        'plan--has-blocks': props.blocks?.length,
      };
    });

    return {
      formattedPrice,
      classes,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan {
  background: #fff;
  padding: 1.875rem 1.5rem;
  border-top: 0.125rem solid transparent;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &--highlight {
    box-shadow: 0 0 0 1px var(--g-color-primary);
    z-index: 1;
  }

  &--disabled:not(:hover):not(:focus-within) > * {
    opacity: 0.5;
  }

  &--has-blocks {
    padding-bottom: 0;
  }
}

.plan__description {
  flex-grow: 1;
}

.plan__description__text {
  p {
    margin-bottom: 1.5rem;
    margin-top: 0.3125rem;
  }
}

.plan__title {
  margin: 0;
}

.plan__price {
  font-size: 1.25rem;
  margin-top: -0.25rem;

  > span {
    font-size: 1.45rem;
  }
}

.plan__actions {
  margin: 0.5rem 0;
  min-height: 5rem;

  &::v-deep .v-button {
    width: 100%;
  }

  &__text {
    font-size: 0.9rem;
    margin-top: 0.25rem;

    &::v-deep .v-inline-link {
      font-size: inherit;
    }
  }
}

.plan__features {
  font-size: 0.925rem;
  margin: 1.25rem 0;
  padding: 0;
  list-style: none;

  li {
    padding-left: 2.5rem;
    line-height: 1.4;
    margin-bottom: 0.75rem;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.plan__feature-icon {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.plan__block {
  border-top: 0.125rem solid var(--g-color-gray-200);
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    font-size: 1.25em;
  }
}
</style>
